import { useState, useRef, useEffect } from 'react';
import { Send, User, Loader2, Play, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import EmailListOverlay from '@/components/common/EmailListOverlay';
import ActionResult from '@/components/common/ActionResult';
import BoswellLogo from '@/components/common/BoswellLogo';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/utils/api';

export default function Boswell() {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m Boswell, your personal email assistant. How can I help you manage your emails today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedChains, setSuggestedChains] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [emailListOverlay, setEmailListOverlay] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (msg) => {
    setMessages(prev => [...prev, msg]);
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');

    addMessage({
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    });
    setIsLoading(true);
    setSuggestedChains([]);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await api.post('/boswell/chat', {
        message: userMessage,
        conversation_history: conversationHistory
      });

      // Add action results BEFORE the AI response
      if (response.data.executed_actions && response.data.executed_actions.length > 0) {
        for (const action of response.data.executed_actions) {
          addMessage({
            role: 'assistant',
            timestamp: new Date().toISOString(),
            isActionResult: true,
            action: action  // Store the full action object
          });
        }
        // Invalidate caches that may have been modified by auto-executed actions
        queryClient.invalidateQueries({ queryKey: ['ai-instructions'] });
      }

      // Add AI response after actions
      addMessage({
        role: 'assistant',
        content: response.data.message,
        timestamp: response.data.timestamp
      });

      if (response.data.suggested_chains && response.data.suggested_chains.length > 0) {
        setSuggestedChains(response.data.suggested_chains);
      }
    } catch (error) {
      console.error('Chat error:', error);
      addMessage({
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteChain = async (chain) => {
    setIsExecuting(true);
    try {
      const response = await api.post('/actions/execute-chain', {
        actions: chain.actions,
        chain_source: chain.chain_source || undefined
      });

      setSuggestedChains(prev => prev.filter(c => c !== chain));

      const results = response.data;

      // Add action results first
      for (const action of results) {
        addMessage({
          role: 'assistant',
          timestamp: new Date().toISOString(),
          isActionResult: true,
          action: action  // Store the full action object
        });
      }

      // Invalidate caches that may have been modified by executed actions
      queryClient.invalidateQueries({ queryKey: ['ai-instructions'] });

      // Feed the chain result back to the AI for follow-up
      const resultText = results.map(r =>
        `Action ${r.action}: ${r.success ? 'OK' : 'FAILED'} - ${r.message || ''}`
      ).join('\n');

      setIsLoading(true);
      const conversationHistory = messages
        .concat(results.map(action => ({
          role: 'assistant',
          content: `Action ${action.action}: ${action.success ? 'OK' : 'FAILED'} - ${action.message || ''}`
        })))
        .map(msg => ({
          role: msg.role,
          content: msg.content || (msg.action ? `Action result: ${msg.action.message}` : '')
        }));

      const followUp = await api.post('/boswell/chat', {
        message: resultText,
        conversation_history: conversationHistory
      });

      // Add AI response after action results
      addMessage({
        role: 'assistant',
        content: followUp.data.message,
        timestamp: followUp.data.timestamp
      });

      if (followUp.data.suggested_chains && followUp.data.suggested_chains.length > 0) {
        setSuggestedChains(followUp.data.suggested_chains);
      }
    } catch (error) {
      console.error('Chain execution error:', error);
      addMessage({
        role: 'assistant',
        content: `Failed to execute chain: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      });
    } finally {
      setIsExecuting(false);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const chainSummary = (chain) => {
    return chain.actions.map(a => a.action).join(' → ');
  };

  /**
   * Parse EMAILS:[id1,id2,id3,...] syntax and render as clickable chips
   * Returns array of text/component segments
   */
  const parseEmailSyntax = (content) => {
    const emailPattern = /EMAILS:\[([^\]]+)\]/g;
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = emailPattern.exec(content)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }

      // Parse email IDs
      const emailIds = match[1].split(',').map(id => id.trim()).filter(id => id);

      // Add email list button
      segments.push({
        type: 'emails',
        emailIds,
        count: emailIds.length
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      segments.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    // If no matches, return single text segment
    return segments.length > 0 ? segments : [{ type: 'text', content }];
  };

  /**
   * Render message content with email list buttons
   */
  const renderMessageContent = (content) => {
    const segments = parseEmailSyntax(content);

    return (
      <div className="text-sm whitespace-pre-wrap">
        {segments.map((segment, index) => {
          if (segment.type === 'text') {
            return <span key={index}>{segment.content}</span>;
          } else if (segment.type === 'emails') {
            return (
              <button
                key={index}
                onClick={() => setEmailListOverlay({
                  emailIds: segment.emailIds,
                  title: `Email List (${segment.count})`
                })}
                className="inline-flex items-center gap-1 px-2 py-1 mx-1 bg-primary/10 hover:bg-primary/20 text-primary rounded text-xs font-medium transition-colors"
              >
                <Mail className="h-3 w-3" />
                View {segment.count} email{segment.count !== 1 ? 's' : ''}
              </button>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <BoswellLogo className="h-6 w-6 sm:h-8 sm:w-8" />
          Boswell
        </h1>
        <p className="hidden sm:block text-sm text-muted-foreground mt-1">
          Your conversational email assistant
        </p>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden min-h-0">
        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 min-h-0">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && !message.isActionResult && (
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center p-1">
                      <BoswellLogo className="h-full w-full" />
                    </div>
                  </div>
                )}

                {message.isActionResult ? (
                  <div className="flex-1 max-w-[90%] sm:max-w-[80%]">
                    <ActionResult action={message.action} />
                  </div>
                ) : (
                  <div
                    className={`rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground max-w-[90%] sm:max-w-[70%]'
                        : message.isError
                        ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 max-w-[90%] sm:max-w-[80%]'
                        : 'bg-muted max-w-[90%] sm:max-w-[80%]'
                    }`}
                  >
                    {renderMessageContent(message.content)}
                  </div>
                )}

                {message.role === 'user' && (
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                    </div>
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center p-1">
                    <BoswellLogo className="h-full w-full" />
                  </div>
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Chains Panel */}
          {suggestedChains.length > 0 && (
            <div className="border-t p-4 bg-blue-50 dark:bg-blue-950/20 space-y-2">
              <p className="text-sm font-medium mb-2 text-blue-700 dark:text-blue-300">
                Review & Approve:
              </p>
              {suggestedChains.map((chain, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between p-3 bg-card rounded border border-blue-200 dark:border-blue-800 gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {chainSummary(chain)}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {chain.actions.map((a, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {a.action}
                        </Badge>
                      ))}
                    </div>
                    {chain.chain_source && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmailListOverlay({
                            emailIds: chain.chain_source.emails.map(e => e.id),
                            title: `${chain.chain_source.ref} - ${chain.chain_source.emails.length} emails`
                          });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 mt-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs font-medium transition-colors"
                      >
                        <Mail className="h-3 w-3" />
                        View {chain.chain_source.emails.length} email{chain.chain_source.emails.length !== 1 ? 's' : ''} from {chain.chain_source.ref}
                      </button>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleExecuteChain(chain)}
                    disabled={isExecuting}
                    className="shrink-0"
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Execute
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Boswell to help with your emails..."
                className="flex-1 resize-none border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
                disabled={isLoading}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email List Overlay */}
      {emailListOverlay && (
        <EmailListOverlay
          emailIds={emailListOverlay.emailIds}
          title={emailListOverlay.title}
          onClose={() => setEmailListOverlay(null)}
        />
      )}
    </div>
  );
}
